/* eslint-disable indent */
import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {MainLoader, DataGrid, ConfirmationModalPromise} from '@components';
import {GreenButton, StyledH3, WhiteButton} from '@styled';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import styled from 'styled-components';
import moment from 'moment';
import {sagaActions} from './redux/saga';

const StyledHeadRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  .heading {
  }
  .actions {
  }
`;

const RowActions = ({data, onAction}) => {
  return (
    <div>
      <WhiteButton
        onClick={() => onAction('edit', data)}
        style={{margin: '.16em'}}
      >
        <i className="fa fa-edit mr-1" />
        Edit
      </WhiteButton>
      <WhiteButton
        onClick={() => onAction('delete', data)}
        style={{margin: '.16em'}}
      >
        <i className="fa fa-trash mr-1" />
        Delete
      </WhiteButton>
    </div>
  );
};

const ManageActivities = () => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const remoteData = useSelector((state) => state.activity.activities);
  const isLoading = useSelector((state) => state.activity.activities.loading);
  const [editRow, setEditRow] = useState(null);
  const [showAddEditModal, setShowAddEditModal] = useState(false);

  const history = useHistory();

  const [pagination, setPagination] = React.useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
    sortOn: 'activityYear',
    sortDir: 'DESC',
  });

  useEffect(() => {
    if (
      remoteData.page !== pagination.page ||
      remoteData.pageSize !== pagination.pageSize ||
      remoteData.totalCount !== pagination.totalCount ||
      remoteData.sortOn !== pagination.sortOn ||
      remoteData.sortDir !== pagination.sortDir
    ) {
      setPagination({
        page: remoteData.page,
        pageSize: remoteData.pageSize,
        totalCount: remoteData.totalCount,
        sortOn: remoteData.sortOn,
        sortDir: remoteData.sortDir,
      });
    }
  }, [remoteData]);

  const fetchData = ({
    page = 1,
    pageSize = pagination.pageSize,
    sortOn,
    sortDir,
  }) => {
    dispatch({
      type: sagaActions.FETCH_ACTIVITIES,
      payload: {
        values: {
          page,
          pageSize,
          sortOn,
          sortDir,
        },
      },
    });
  };

  const handleDeleteActivity = (remove) => {
    dispatch({
      type: sagaActions.REMOVE_ACTIVITY,
      payload: {
        activityId: remove.id,
        values: {},
      },
    });
  };

  const handleRowAction = useCallback(
    (action, row) => {
      switch (action) {
        case 'edit': {
          setEditRow(row);
          setShowAddEditModal(true);
          break;
        }
        case 'delete': {
          ConfirmationModalPromise({
            title: `Are you sure you want to delete this [${row.id}] activity?`,
            show: true,
            confirmBtn: 'Yes',
            cancelBtn: 'No',
            headTitle: 'Confirmation!',
          }).then(
            () => {
              handleDeleteActivity(row);
            },
            () => {
              //  do nothing on failure
            }
          );
          break;
        }
        default:
          break;
      }
    },
    [editRow, showAddEditModal]
  );

  const handleAddActivity = () => {
    history.push('/activities/add');
  };

  const renderRowActions = (colProps) => {
    return <RowActions data={colProps} onAction={handleRowAction} />;
  };

  const Columns = React.useMemo(
    () => [
      {
        accessor: 'id',
        Header: 'Activity ID',
        className: 'grey-column d-none d-lg-block',
        width: 50,
        tooltip: false,
      },
      {
        accessor: 'entityId',
        Header: 'Entity Name',
        tooltip: false,
        className: 'd-none d-lg-block',
        Cell: ({row}) => row.original.entityName,
      },
      {
        accessor: 'createdOn',
        Header: 'Upload Date',
        tooltip: false,
        className: 'd-none d-lg-block',
        Cell: ({value}) => (value ? moment(value).fromNow() : ''),
      },
      {
        accessor: 'activityDesc',
        Header: 'Activity',
        tooltip: false,
        className: 'd-none d-lg-block',
      },
      {
        accessor: 'status',
        Header: 'Status',
        tooltip: false,
        className: 'd-none d-lg-block',
      },
      {
        id: 'actions',
        Header: <div className="d-none d-lg-block">Actions</div>,
        accessor: renderRowActions,
        disableSortBy: true,
        disableFilters: true,
        width: 50,
      },
    ],
    [pagination]
  );

  return (
    <>
      {isLoading && (
        <MainLoader
          minHeight="100vh"
          spinnerColor="#479541"
          background="rgba(0,0,0,0.2)"
        />
      )}
      <div>
        <StyledHeadRow>
          <StyledH3>{t('reporter.activity.list.pageHeading')}</StyledH3>
          <div className="actions">
            <GreenButton className="ml-1" onClick={handleAddActivity}>
              <i className="fa fa-plus mr-1" />
              {t('reporter.activity.list.addActivity')}
            </GreenButton>
          </div>
        </StyledHeadRow>
        <DataGrid
          columns={Columns}
          data={remoteData.data}
          onFetchData={fetchData}
          pagination={pagination}
          filtering={false}
          expandEnabled={false}
        />
      </div>
    </>
  );
};

export default ManageActivities;
